import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api, extractError } from "../lib/api";
import { useWS } from "../context/WebSocketContext";
import { contractSelectOptionLabel, contractSelectShortLabel } from "../lib/purchaseContractOptions";
import { PageHeader, SectionCard, StatePill, Pill } from "../components/Ui";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { Scale, Radio, Plus, Trash2, Search } from "lucide-react";
import { Checkbox } from "../components/ui/checkbox";
import PurchaseTraceLine from "../components/PurchaseTraceLine";
import WeighmentReceiptActions from "../components/WeighmentReceiptActions";
import OutwardWeighmentReceiptActions from "../components/OutwardWeighmentReceiptActions";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import DataTableBlock from "../components/DataTableBlock";
import { useBootstrapList } from "../hooks/useBootstrapList";
import { useDataTable } from "../hooks/useDataTable";
import { LIST_PAGE_SIZE_OPTIONS } from "../lib/listPagination";
import { WEIGHMENT_REGISTER_COLUMNS } from "./weighbridgeColumns";
import { OUTWARD_WEIGHMENT_REGISTER_COLUMNS } from "./outwardDispatchColumns";
import { formatListDate, gatePassTraceMeta } from "../lib/purchaseListMeta";
import { formatIndiaDateTime } from "../lib/indiaTime";
import { useAuth, hasRole } from "../context/AuthContext";
import { contractsUnloadByQtl, purchaseNeedsRouteAfterGross, purchaseNeedsWeightRecording, purchaseWeighKind, purchaseWeighStepLabel } from "../lib/unloadingHelpers";
import { formatKg, formatWeightKg, kgToQtl, qtlToKg } from "../lib/weightUnits";
import { applyWeightToInputElement, fetchScaleWeightAndCameraSnapshots, weighPhotoDisplaySrc } from "../lib/localScaleAgent";
import LiveWeighbridgeHUD from "../components/LiveWeighbridgeHUD";
import { useLiveScale } from "../hooks/useLiveScale";
import { isCloudScaleCaptureBlocked } from "../lib/liveScale";
import AdminWeighCorrection from "../components/AdminWeighCorrection";
import OutwardWeighCorrection from "../components/OutwardWeighCorrection";
import AccountsLinkedContractsEditor from "../components/AccountsLinkedContractsEditor";
import WeighEvidencePanel from "../components/WeighEvidencePanel";
import { reservationBillableForContract } from "../lib/mandiSettlement";
import { weighbridgeModuleFromKey } from "../lib/weighbridgeModuleConfig";
import MobilePillTabBar from "../components/mobile/MobilePillTabBar";
import MobileUnderlineTabBar from "../components/mobile/MobileUnderlineTabBar";
import StoreWeighQueue from "../components/StoreWeighQueue";

function weightKgToRecord(weight, capture) {
  const typed = parseFloat(weight);
  if (Number.isFinite(typed) && typed > 0) return typed;
  const locked = Number(capture?.weightKg);
  if (Number.isFinite(locked) && locked > 0) return locked;
  return NaN;
}

function scaleCaptureState(capture) {
  return {
    weightKg: capture.weightKg,
    weighedAt: capture.weighedAt,
    camera1SnapshotUrl: capture.camera1SnapshotUrl,
    camera2SnapshotUrl: capture.camera2SnapshotUrl,
    photoWarning: capture.photoWarning,
    photosCaptured: capture.photosCaptured,
  };
}

export default function WeighbridgePage({ moduleKey = null }) {
  const moduleConfig = moduleKey ? weighbridgeModuleFromKey(moduleKey) : null;
  const fixedTab = moduleConfig?.tab || null;
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const isAdmin = hasRole(user, "admin");
  const isWeighbridge = hasRole(user, "weighbridge");
  const canEditWeights = isAdmin || isWeighbridge;
  const [gatePasses, setGatePasses] = useState([]);
  const [pendingContracts, setPendingContracts] = useState([]);
  const [loadingContracts, setLoadingContracts] = useState(false);
  const [queuePurchases, setQueuePurchases] = useState([]);
  const [storeQueue, setStoreQueue] = useState({ gross: [], tare: [] });
  const [outwardQueue, setOutwardQueue] = useState({ tare: [], gross: [] });
  const [outwardRegister, setOutwardRegister] = useState([]);
  const [supervisors, setSupervisors] = useState([]);
  const [form, setForm] = useState({
    gate_pass_id: "",
    party_id: "",
    party_name: "",
    party_net_weight_kg: "",
    lab_required: true,
  });
  const [contractLines, setContractLines] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [gatePassSearch, setGatePassSearch] = useState("");
  const [activeTab, setActiveTab] = useState(fixedTab || "operations");
  const [registerSelectedId, setRegisterSelectedId] = useState(() => searchParams.get("purchase") || null);
  const [registerEditOpen, setRegisterEditOpen] = useState(false);
  const [registerLinkEditId, setRegisterLinkEditId] = useState(null);
  const [registerLinkPurchase, setRegisterLinkPurchase] = useState(null);
  const [registerLinkLoading, setRegisterLinkLoading] = useState(false);
  const [registerLinkSaving, setRegisterLinkSaving] = useState(false);
  const registerLinkEditorRef = useRef(null);
  const [outwardRegisterSelectedId, setOutwardRegisterSelectedId] = useState(() => searchParams.get("dispatch") || null);
  const [outwardRegisterEditOpen, setOutwardRegisterEditOpen] = useState(false);
  const [weighmentRegisterCount, setWeighmentRegisterCount] = useState(0);
  const [registerSubTab, setRegisterSubTab] = useState("inward");
  const [outwardSubTab, setOutwardSubTab] = useState("tare");
  const [inwardSubTab, setInwardSubTab] = useState("record");
  const [focusPurchaseId, setFocusPurchaseId] = useState(() => searchParams.get("purchase") || "");
  const [focusDispatchId, setFocusDispatchId] = useState(() => searchParams.get("dispatch") || "");

  useEffect(() => {
    const purchase = searchParams.get("purchase");
    const gatePass = searchParams.get("gate_pass");
    const dispatch = searchParams.get("dispatch");
    if (purchase) {
      setFocusPurchaseId(String(purchase));
      setRegisterSelectedId(purchase);
      setActiveTab(fixedTab || "operations");
      setInwardSubTab("record");
    }
    if (dispatch) {
      setFocusDispatchId(String(dispatch));
      setOutwardRegisterSelectedId(dispatch);
      setActiveTab(fixedTab || "operations");
      setOutwardSubTab("tare");
    }
    if (gatePass) {
      setForm((f) => ({ ...f, gate_pass_id: String(gatePass) }));
      setActiveTab(fixedTab || "operations");
      setInwardSubTab("start");
    }
  }, [searchParams, fixedTab]);

  const weighmentList = useBootstrapList({
    path: "/purchases/bootstrap",
    columns: WEIGHMENT_REGISTER_COLUMNS,
    defaultSort: { id: "created_at", desc: true },
    extraParams: { has_weighment: 1, view: "weigh_register" },
    enabled: activeTab === "register" || moduleKey === "register",
    wsTypes: ["purchase"],
  });

  const contractLabel = (c) => contractSelectOptionLabel(c);

  const contractForLine = (ln) =>
    contractsForSelection.find((x) => String(x.id) === String(ln.purchase_contract_id))
    || pendingContracts.find((x) => String(x.id) === String(ln.purchase_contract_id));

  const toContractLine = (c) => ({
    purchase_contract_id: String(c.id),
    contract_number: c.contract_number,
    quality: c.quality,
    price: c.price,
    balance_qtl: c.balance_qtl,
    quantity: c.quantity,
    planned_qtl: "",
    bags: "",
  });

  const contractsLinkedToGatePass = (contracts, gatePassId) => {
    if (!gatePassId) return [];
    return contracts.filter(
      (c) => c.gate_pass_id != null && String(c.gate_pass_id) === String(gatePassId),
    );
  };

  const load = useCallback(() => {
    const module =
      moduleKey
      || (activeTab === "outward"
        ? "outward"
        : activeTab === "register"
          ? "register"
          : "inward");
    api
      .get("/weighbridge/bootstrap", { params: { module } })
      .then((r) => {
        const d = r.data || {};
        if (module === "inward" || module === "all") {
          setGatePasses(d.gate_passes || []);
          const q = d.queue || {};
          const merged = [...(q.gross || []), ...(q.route || []), ...(q.mid || []), ...(q.tare || [])];
          setQueuePurchases(merged);
          setSupervisors(d.supervisors || []);
          setStoreQueue(d.store_queue || { gross: [], tare: [] });
        }
        if (module === "outward" || module === "all") {
          setOutwardQueue(d.outward_queue || { tare: [], gross: [] });
        }
        if (module === "register" || module === "all") {
          setOutwardRegister(d.outward_weighment_register || []);
        }
        if (d.weighment_register_count != null) {
          setWeighmentRegisterCount(d.weighment_register_count);
        } else if (d.outward_weighment_register_count != null) {
          setWeighmentRegisterCount(d.outward_weighment_register_count);
        }
      })
      .catch(() => {
        if (module === "inward" || module === "all") {
          setGatePasses([]);
          setQueuePurchases([]);
          setSupervisors([]);
          setStoreQueue({ gross: [], tare: [] });
        }
        if (module === "outward" || module === "all") {
          setOutwardQueue({ tare: [], gross: [] });
        }
        if (module === "register" || module === "all") {
          setOutwardRegister([]);
        }
      });
  }, [moduleKey, activeTab]);

  useEffect(() => {
    if (fixedTab) setActiveTab(fixedTab);
  }, [fixedTab]);

  useEffect(() => {
    load();
  }, [load]);

  const { subscribe } = useWS();
  useEffect(() => subscribe("purchase", load), [subscribe, load]);
  useEffect(() => subscribe("purchase", weighmentList.reload), [subscribe, weighmentList.reload]);
  useEffect(() => subscribe("gate_pass", load), [subscribe, load]);
  useEffect(() => subscribe("store_grn", load), [subscribe, load]);
  useEffect(() => subscribe("outward_dispatch", load), [subscribe, load]);

  const loadPendingForTruck = useCallback(() => {
    const pid = form.party_id ? Number(form.party_id) : null;
    const gpid = form.gate_pass_id || "";
    if (!pid) {
      setPendingContracts([]);
      setContractLines([]);
      return;
    }
    setLoadingContracts(true);
    api
      .get("/purchase-contracts/pending-for-weighbridge", { params: { party_id: pid } })
      .then((r) => {
        const contracts = r.data || [];
        setPendingContracts(contracts);
        const linked = contractsLinkedToGatePass(contracts, gpid);
        setContractLines((prev) => {
          if (linked.length > 0) {
            return linked.map(toContractLine);
          }
          const stillValid = prev.filter((ln) =>
            contracts.some((c) => String(c.id) === String(ln.purchase_contract_id)),
          );
          if (stillValid.length > 0) return stillValid;
          // Pre-arrival contracts are party-level (no gate_pass_id) — still show the latest active one.
          return contracts.length > 0 ? [toContractLine(contracts[0])] : [];
        });
        const first = linked[0] || contracts[0];
        if (first) {
          setForm((f) => ({
            ...f,
            lab_required:
              first.contract_phase === "ARRIVAL" && first.purchase_type === "ON_ARRIVAL_DIRECT_PURCHASE"
                ? false
                : f.lab_required,
          }));
        }
      })
      .catch(() => {
        setPendingContracts([]);
        setContractLines([]);
      })
      .finally(() => setLoadingContracts(false));
  }, [form.party_id, form.gate_pass_id]);

  useEffect(() => {
    loadPendingForTruck();
  }, [loadPendingForTruck]);

  useEffect(() => subscribe("contract", loadPendingForTruck), [subscribe, loadPendingForTruck]);

  const onGatePassChange = (v) => {
    const gp = gatePasses.find((g) => String(g.id) === String(v));
    setForm({
      ...form,
      gate_pass_id: v,
      party_id: gp?.party_id != null && gp.party_id !== "" ? String(gp.party_id) : "",
      party_name: gp?.party_name || "",
    });
    // Clear lines immediately so the next party load does not keep the previous truck's contracts.
    setContractLines([]);
    setPendingContracts([]);
  };

  const contractMeta = (ln) => {
    const c = pendingContracts.find((x) => String(x.id) === String(ln.purchase_contract_id));
    return {
      contract: c,
      quantity: Number(ln.quantity ?? c?.quantity) || 0,
      balance_qtl: ln.balance_qtl ?? c?.balance_qtl,
    };
  };

  const contractsForSelection = useMemo(() => {
    const linked = contractsLinkedToGatePass(pendingContracts, form.gate_pass_id);
    return linked.length > 0 ? linked : pendingContracts;
  }, [pendingContracts, form.gate_pass_id]);

  const addContractLine = () => {
    const used = new Set(contractLines.map((l) => String(l.purchase_contract_id)));
    const next = contractsForSelection.find((c) => !used.has(String(c.id)));
    if (!next) {
      toast.error("No more contracts to add for this party");
      return;
    }
    const isFirst = contractLines.length === 0;
    setContractLines((prev) => [...prev, toContractLine(next)]);
    if (isFirst) {
      setForm((f) => ({
        ...f,
        lab_required:
          next.contract_phase === "ARRIVAL" && next.purchase_type === "ON_ARRIVAL_DIRECT_PURCHASE"
            ? false
            : f.lab_required,
      }));
    }
  };

  const updateContractLine = (idx, field, value) => {
    setContractLines((prev) => {
      const next = [...prev];
      next[idx] = { ...next[idx], [field]: value };
      if (field === "purchase_contract_id") {
        const c = pendingContracts.find((x) => String(x.id) === value);
        if (c) {
          next[idx].contract_number = c.contract_number;
          next[idx].quality = c.quality;
          next[idx].price = c.price;
          next[idx].balance_qtl = c.balance_qtl;
          next[idx].quantity = c.quantity;
        }
      }
      return next;
    });
  };

  const removeContractLine = (idx) => setContractLines((prev) => prev.filter((_, i) => i !== idx));

  const looseTruck = contractsUnloadByQtl(contractLines, pendingContracts);

  const hasContracts = contractLines.length > 0;
  const multiContract = contractLines.length > 1;
  const partyWeightQtl = kgToQtl(form.party_net_weight_kg);
  const partyWeightOk = partyWeightQtl != null && partyWeightQtl > 0;

  const selectedGatePass = gatePasses.find((g) => String(g.id) === String(form.gate_pass_id));
  const isSupplierSupply =
    String(selectedGatePass?.party_kind || "").toUpperCase() === "SUPPLIER" ||
    pendingContracts.some((c) => String(c.party_kind || "").toUpperCase() === "SUPPLIER");
  const partyWeightRequired = isSupplierSupply;
  const lineBagsTotal = useMemo(() => {
    if (looseTruck) return 0;
    return contractLines.reduce((s, l) => {
      const n = Number(l.bags);
      return s + (Number.isFinite(n) && n > 0 ? Math.round(n) : 0);
    }, 0);
  }, [contractLines, looseTruck]);
  // Prefer bags split across contracts; fall back to gate-pass expected bags.
  const reportedBags = looseTruck
    ? 0
    : (lineBagsTotal > 0 ? lineBagsTotal : Number(selectedGatePass?.expected_bags || 0));

  const lineReservation = useMemo(() => {
    if (!hasContracts) return [];
    return contractLines.map((ln) => {
      const { contract, quantity } = contractMeta(ln);
      if (!contract) return null;
      const lineBags =
        ln.bags !== "" && ln.bags != null && Number.isFinite(Number(ln.bags))
          ? Math.max(0, Math.round(Number(ln.bags)))
          : null;
      const shareBags = lineBags != null
        ? lineBags
        : (multiContract ? 0 : reportedBags);
      // Optional party weight: show reserve preview only when entered.
      if (!partyWeightOk) {
        if (shareBags <= 0) return null;
        return {
          ln,
          contract,
          shareQtl: null,
          shareBags,
          billableQtl: null,
          bardanaQtl: null,
        };
      }
      const bagDenom = lineBagsTotal > 0 ? lineBagsTotal : reportedBags;
      const shareQtl = multiContract
        ? (quantity > 0 && partyWeightQtl > 0
          ? (lineBags != null && bagDenom > 0
            ? Math.round((partyWeightQtl * (lineBags / bagDenom)) * 100) / 100
            : Math.round((partyWeightQtl / contractLines.length) * 100) / 100)
          : partyWeightQtl)
        : partyWeightQtl;
      if (!shareQtl || shareQtl <= 0) return null;
      const detail = reservationBillableForContract(
        contract,
        shareQtl,
        shareBags > 0 ? shareBags : reportedBags,
        selectedGatePass?.party_bardana_rules ?? null,
      );
      return { ln, contract, shareQtl, shareBags: shareBags > 0 ? shareBags : reportedBags, ...detail };
    }).filter(Boolean);
  }, [
    hasContracts,
    contractLines,
    pendingContracts,
    partyWeightQtl,
    partyWeightOk,
    reportedBags,
    lineBagsTotal,
    multiContract,
    selectedGatePass,
  ]);

  const totalBillableReserved = lineReservation.reduce((s, row) => s + (row.billableQtl || 0), 0);

  const canStart = Boolean(
    form.gate_pass_id
    && form.party_id
    && hasContracts
    && pendingContracts.length > 0
    && (!partyWeightRequired || partyWeightOk),
  );

  const startPurchase = async (e) => {
    e.preventDefault();
    if (!form.party_id) {
      toast.error("Gate pass must be linked to a party (farmer/supplier)");
      return;
    }
    if (!pendingContracts.length) {
      toast.error("No active purchase contract for this party — register one under Purchase Contracts first");
      return;
    }
    if (!contractLines.length) {
      toast.error("Link at least one purchase contract for this truck");
      return;
    }
    const ids = contractLines.map((l) => l.purchase_contract_id);
    if (new Set(ids).size !== ids.length) {
      toast.error("Each contract can only appear once on this truck");
      return;
    }
    const gatePass = gatePasses.find((g) => String(g.id) === String(form.gate_pass_id));
    const bagsFromLines = contractLines.reduce((s, l) => {
      const n = Number(l.bags);
      return s + (Number.isFinite(n) && n > 0 ? Math.round(n) : 0);
    }, 0);
    const bagsReported = looseTruck
      ? 0
      : (bagsFromLines > 0 ? bagsFromLines : Number(gatePass?.expected_bags || 0));
    if (!looseTruck && bagsReported <= 0) {
      toast.error(
        multiContract
          ? "Enter bags on each contract (or set bags on the gate pass)"
          : "Bags required — set bags on the gate pass, or add bags on the contract",
      );
      return;
    }
    if (multiContract && !looseTruck && bagsFromLines > 0) {
      const missing = contractLines.some(
        (l) => l.bags === "" || l.bags == null || !Number.isFinite(Number(l.bags)) || Number(l.bags) < 0,
      );
      if (missing) {
        toast.error("Enter bags on every linked contract (or leave all blank to use gate-pass bags)");
        return;
      }
    }
    if (partyWeightRequired && !partyWeightOk) {
      toast.error("Party side weight is required for supplier supply");
      return;
    }
    try {
      setSubmitting(true);
      await api.post("/purchases/start", {
        gate_pass_id: Number(form.gate_pass_id),
        party_id: Number(form.party_id),
        party_name: form.party_name,
        bags_reported: bagsReported,
        lab_required: form.lab_required,
        ...(partyWeightOk ? { party_net_weight_qtl: partyWeightQtl } : {}),
        allocations: contractLines.map((l) => {
          const bags =
            l.bags !== "" && l.bags != null && Number.isFinite(Number(l.bags))
              ? Math.max(0, Math.round(Number(l.bags)))
              : undefined;
          return {
            purchase_contract_id: Number(l.purchase_contract_id),
            ...(bags != null ? { party_invoice_bags: bags } : {}),
          };
        }),
      });
      toast.success("Purchase started — record gross weight");
      setForm({
        gate_pass_id: "",
        party_id: "",
        party_name: "",
        party_net_weight_kg: "",
        lab_required: true,
      });
      setContractLines([]);
      setPendingContracts([]);
      load();
    } catch (err) {
      toast.error(extractError(err));
    } finally {
      setSubmitting(false);
    }
  };

  const filteredGatePasses = useMemo(() => {
    const q = gatePassSearch.trim().toLowerCase();
    if (!q) return gatePasses;
    return gatePasses.filter((g) =>
      [g.code, g.vehicle_number, g.party_name, g.expected_variety, formatListDate(g.created_at)]
        .some((v) => String(v || "").toLowerCase().includes(q)),
    );
  }, [gatePasses, gatePassSearch]);

  const weighmentRows = useMemo(() => weighmentList.table.rows, [weighmentList.table.rows]);
  const outwardRegisterTable = useDataTable(
    outwardRegister,
    OUTWARD_WEIGHMENT_REGISTER_COLUMNS,
    { id: "weigh_date", desc: true },
  );
  const weighmentRegisterTabCount = activeTab === "register" || moduleKey === "register"
    ? weighmentList.meta.count
    : weighmentRegisterCount;

  const registerSelected = useMemo(
    () => weighmentRows.find((p) => String(p.id) === String(registerSelectedId)) || null,
    [weighmentRows, registerSelectedId],
  );

  const canAdminEditPurchaseLink = (row) => isAdmin && row && row.state !== "REJECTED";

  const closePurchaseLinkEdit = useCallback(() => {
    setRegisterLinkEditId(null);
    setRegisterLinkPurchase(null);
    setRegisterLinkLoading(false);
  }, []);

  const openPurchaseLinkEdit = useCallback(
    async (row) => {
      if (!row?.id) return;
      setRegisterSelectedId(row.id);
      setRegisterEditOpen(false);
      if (String(registerLinkEditId) === String(row.id)) {
        closePurchaseLinkEdit();
        return;
      }
      setRegisterLinkEditId(row.id);
      setRegisterLinkLoading(true);
      setRegisterLinkPurchase(null);
      try {
        const { data } = await api.get(`/purchases/${row.id}`);
        setRegisterLinkPurchase(data);
      } catch (err) {
        toast.error(extractError(err) || "Could not load purchase contracts");
        setRegisterLinkEditId(null);
      } finally {
        setRegisterLinkLoading(false);
      }
    },
    [registerLinkEditId, closePurchaseLinkEdit],
  );

  useEffect(() => {
    if (!form.gate_pass_id) return;
    const gp = gatePasses.find((g) => String(g.id) === String(form.gate_pass_id));
    if (!gp) {
      // Gate pass list not loaded yet, or vehicle already started / left the start queue.
      if (gatePasses.length > 0) {
        setForm((f) => ({
          ...f,
          gate_pass_id: "",
          party_id: "",
          party_name: "",
        }));
        setContractLines([]);
        setPendingContracts([]);
      }
      return;
    }
    // Keep party in sync (also covers deep-link ?gate_pass= that only set id).
    const nextPartyId = gp.party_id != null && gp.party_id !== "" ? String(gp.party_id) : "";
    if (String(form.party_id || "") !== nextPartyId || (gp.party_name && form.party_name !== gp.party_name)) {
      setForm((f) => ({
        ...f,
        party_id: nextPartyId,
        party_name: gp.party_name || f.party_name || "",
      }));
    }
  }, [gatePasses, form.gate_pass_id, form.party_id, form.party_name]);

  const canRouteTrucks = isAdmin || hasRole(user, "manager");

  const weightRecordingQueue = queuePurchases.filter(purchaseNeedsWeightRecording);
  const weighGrossReady = queuePurchases.filter(purchaseNeedsRouteAfterGross);
  const outwardQueueCount = (outwardQueue.tare?.length || 0) + (outwardQueue.gross?.length || 0);

  return (
    <div className="space-y-6 sm:space-y-8" data-testid={moduleConfig?.testId || "weighbridge-page"}>
      <PageHeader
        eyebrow={moduleConfig?.eyebrow || "Weighbridge Operator"}
        title={moduleConfig?.title || "Weighbridge Console"}
        description={
          moduleConfig?.description
            || "Link gate pass to purchase contract(s) and record bridge weights. Routing to lab or unloading is handled by supervisor/manager."
        }
      />


      <Tabs
        value={activeTab}
        onValueChange={moduleConfig ? () => {} : setActiveTab}
        className="space-y-4"
      >
        {!moduleConfig && (
        <TabsList className="bg-card border border-border p-1 h-auto flex-wrap">
          <TabsTrigger value="operations" className="text-xs sm:text-sm">Inward</TabsTrigger>
          <TabsTrigger value="outward" className="text-xs sm:text-sm">
            Outward ({outwardQueueCount})
          </TabsTrigger>
        </TabsList>
        )}

        <TabsContent value="operations" className="space-y-6 sm:space-y-8 mt-0">
      {(moduleKey === "inward" || (!moduleConfig && activeTab === "operations")) && (
        <StoreWeighQueue queue={storeQueue} onDone={load} />
      )}
      {(moduleKey === "inward" || (!moduleConfig && activeTab === "operations")) && (
        <MobileUnderlineTabBar
          tabs={[
            {
              id: "record",
              label: "Record weight",
              badge: weightRecordingQueue.length + weighGrossReady.length,
            },
            {
              id: "start",
              label: "Start purchase",
              badge: gatePasses.length,
            },
          ]}
          value={inwardSubTab}
          onChange={setInwardSubTab}
          testId="weighbridge-inward-subtabs"
        />
      )}
      <div className={inwardSubTab === "record" ? "hidden lg:block" : undefined}>
      <SectionCard title="Link contracts & start purchase">
        <form onSubmit={startPurchase} className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="start-purchase-form">
          <Field label="Gate pass" required className="md:col-span-3">
            <div className="relative mb-2">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
              <Input
                className="pl-8 h-9 text-xs"
                placeholder="Search by date, vehicle, gate pass, party…"
                value={gatePassSearch}
                onChange={(e) => setGatePassSearch(e.target.value)}
                data-testid="gatepass-search"
              />
            </div>
            {filteredGatePasses.length === 0 ? (
              <p className="text-xs text-muted-foreground py-4 text-center rounded-md border border-dashed border-border">
                {gatePasses.length === 0
                  ? "No vehicles ready to start a purchase"
                  : "No gate passes match your search"}
              </p>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-[280px] overflow-y-auto pr-0.5">
                {filteredGatePasses.map((g) => {
                  const selected = String(form.gate_pass_id) === String(g.id);
                  return (
                    <button
                      key={g.id}
                      type="button"
                      onClick={() => onGatePassChange(String(g.id))}
                      className={`text-left rounded-md border px-3 py-2.5 transition-colors min-w-0 ${
                        selected
                          ? "border-sky-400 bg-sky-50/80 ring-1 ring-sky-200"
                          : "border-border bg-card hover:bg-muted/40"
                      }`}
                      data-testid={`gatepass-option-${g.code}`}
                    >
                      <p className="font-mono text-xs font-semibold break-all">{g.code}</p>
                      <p className="text-sm font-medium mt-0.5 truncate">
                        {String(g.vehicle_number || "—").toUpperCase()}
                      </p>
                      <p className="text-[10px] text-muted-foreground mt-0.5 break-words">
                        {gatePassTraceMeta(g)}
                        {g.party_name ? ` · ${g.party_name}` : ""}
                      </p>
                    </button>
                  );
                })}
              </div>
            )}
          </Field>

          <div className="md:col-span-3 space-y-3">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">
                Purchase contracts<span className="text-red-500 ml-0.5">*</span>
              </Label>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={addContractLine}
                disabled={!form.party_id || loadingContracts || contractsForSelection.length === 0}
                data-testid="add-contract-line"
              >
                <Plus className="h-3.5 w-3.5 mr-1" /> Add contract
              </Button>
            </div>
            {contractLines.length === 0 && (
              <p className="text-xs text-muted-foreground">
                {form.gate_pass_id && !loadingContracts
                  ? pendingContracts.length > 0
                    ? `${pendingContracts.length} active contract(s) for this party — click Add contract to link.`
                    : "No active contract for this party — register one under Purchase Contracts."
                  : "Select a gate pass to load contracts for that party."}
              </p>
            )}
            {hasContracts && (
              <div className="rounded-md border border-bansal-sage/30 bg-muted/40 p-3">
                <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">
                  Party side weight (kg)
                  {partyWeightRequired ? (
                    <span className="text-red-500 ml-0.5">*</span>
                  ) : (
                    <span className="normal-case tracking-normal text-muted-foreground font-normal ml-1">(optional)</span>
                  )}
                </Label>
                <Input
                  type="number"
                  step="1"
                  min="0"
                  className="mt-1 h-9 text-sm max-w-xs bg-card"
                  placeholder={partyWeightRequired ? "e.g. 35000 from party weighbridge slip" : "e.g. 35000 — can enter later in accounts"}
                  value={form.party_net_weight_kg}
                  onChange={(e) => setForm({ ...form, party_net_weight_kg: e.target.value })}
                  data-testid="party-net-weight-kg"
                  required={partyWeightRequired}
                />
                <p className="text-[10px] text-muted-foreground mt-1.5">
                  {partyWeightRequired
                    ? "Compulsory for supplier supply. Party weighbridge net: "
                    : "Party weighbridge net: "}
                  {partyWeightQtl != null ? (
                    <span className="font-medium text-foreground tabular">{partyWeightQtl} qtl</span>
                  ) : (
                    "—"
                  )}
                  {reportedBags > 0 && (
                    <>
                      {" "}
                      · gate bags: <span className="font-medium tabular">{reportedBags}</span>
                    </>
                  )}
                  {lineReservation.some((r) => r.billableQtl != null) && (
                    <>
                      {" "}
                      · billable reserved:{" "}
                      <span className="font-medium text-primary tabular">
                        {totalBillableReserved.toFixed(2)} qtl
                      </span>
                      {lineReservation[0]?.bardanaQtl != null && (
                        <>
                          {" "}
                          (bardana {lineReservation[0].bardanaQtl.toFixed(2)} qtl deducted from party net)
                        </>
                      )}
                    </>
                  )}
                </p>
              </div>
            )}
            {contractLines.map((ln, idx) => {
              const { balance_qtl: lineBal } = contractMeta(ln);
              const reserveRow = lineReservation.find(
                (r) => String(r.ln.purchase_contract_id) === String(ln.purchase_contract_id),
              );
              return (
              <div
                key={`${ln.purchase_contract_id}-${idx}`}
                className="rounded-md border border-border p-3 bg-muted/20 space-y-2 min-w-0"
              >
                <div className="flex items-start justify-between gap-2 min-w-0">
                  <div className="flex-1 min-w-0">
                    <Label className="text-[10px] text-muted-foreground">Contract</Label>
                    <Select
                      value={ln.purchase_contract_id}
                      onValueChange={(v) => updateContractLine(idx, "purchase_contract_id", v)}
                    >
                      <SelectTrigger className="mt-1 w-full min-w-0 [&>span]:truncate" data-testid={`contract-line-${idx}`}>
                        <SelectValue placeholder="Select contract…">
                          {contractSelectShortLabel(contractForLine(ln))}
                        </SelectValue>
                      </SelectTrigger>
                      <SelectContent className="max-w-[min(100vw-2rem,28rem)]">
                        {contractsForSelection.map((c) => (
                          <SelectItem
                            key={c.id}
                            value={String(c.id)}
                            className="truncate"
                            disabled={contractLines.some(
                              (l, i) => i !== idx && String(l.purchase_contract_id) === String(c.id),
                            )}
                          >
                            {contractLabel(c)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {lineBal != null && (
                      <p className="text-[10px] text-muted-foreground mt-1">
                        Available: {lineBal} qtl
                        {reserveRow?.billableQtl != null && (
                          <>
                            {" "}
                            · billable reserve:{" "}
                            <span className="font-medium tabular">{reserveRow.billableQtl.toFixed(2)} qtl</span>
                            {reserveRow.shareBags > 0 && ` (${reserveRow.shareBags} bags)`}
                          </>
                        )}
                        {reserveRow?.shareBags > 0 && reserveRow?.billableQtl == null && (
                          <>
                            {" "}
                            · bags on this contract:{" "}
                            <span className="font-medium tabular">{reserveRow.shareBags}</span>
                          </>
                        )}
                      </p>
                    )}
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="shrink-0 h-8 w-8 text-muted-foreground hover:text-red-600"
                    onClick={() => removeContractLine(idx)}
                    disabled={contractLines.length <= 1}
                    aria-label="Remove contract"
                    data-testid={`remove-contract-line-${idx}`}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
                {multiContract && !looseTruck && (
                  <div>
                    <Label className="text-[10px] text-muted-foreground">
                      Bags on this contract
                      {Number(selectedGatePass?.expected_bags || 0) <= 0 ? (
                        <span className="text-red-500 ml-0.5">*</span>
                      ) : (
                        <span className="normal-case tracking-normal ml-1">(optional)</span>
                      )}
                    </Label>
                    <Input
                      type="number"
                      step="1"
                      min="0"
                      className="mt-1 h-8 text-xs max-w-xs"
                      placeholder="e.g. how many bags for this contract"
                      value={ln.bags ?? ""}
                      onChange={(e) => updateContractLine(idx, "bags", e.target.value)}
                      data-testid={`contract-bags-${idx}`}
                    />
                  </div>
                )}
              </div>
            );
            })}
            {form.party_id && !loadingContracts && pendingContracts.length === 0 && (
              <p className="text-xs text-amber-700">
                No pending contract for this party. Register under{" "}
                <Link to="/contracts" className="font-medium underline">Purchase contracts</Link>.
              </p>
            )}
          </div>

          {looseTruck && (
            <div className="md:col-span-3 rounded-md border border-border bg-muted/30 p-3 text-sm text-muted-foreground">
              <span className="font-medium text-foreground">Loose contract</span> — net qtl is recorded at weighbridge;
              unloading splits by qtl.
            </div>
          )}
          <div className="md:col-span-3 flex items-start gap-3 rounded-md border border-border bg-muted/30 p-3">
            <Checkbox
              id="lab-required-cb"
              checked={form.lab_required}
              onCheckedChange={(v) => setForm({ ...form, lab_required: v === true })}
              data-testid="lab-required-checkbox"
            />
            <div className="grid gap-0.5">
              <Label htmlFor="lab-required-cb" className="text-sm font-medium cursor-pointer leading-snug">
                Require truck lab before unloading
              </Label>
              <span className="text-xs text-muted-foreground">
                Turn off for loads that go straight to approval/unload without incoming moisture lab.
              </span>
            </div>
          </div>
          <div className="md:col-span-3 flex justify-stretch sm:justify-end">
            <Button
              type="submit"
              disabled={submitting || !canStart}
              className="bg-bansal-sage hover:bg-bansal-sageDark text-white w-full sm:w-auto min-h-[44px]"
              data-testid="start-purchase-submit"
            >
              {submitting ? "Starting…" : "Start purchase"}
            </Button>
          </div>
        </form>
      </SectionCard>
      </div>

      <div className={inwardSubTab === "start" ? "hidden lg:block" : undefined}>
      <SectionCard title={`Record weight (${weightRecordingQueue.length})`} testId="weight-recording">
        <p className="text-xs text-muted-foreground mb-3 px-1">
          Enter the bridge reading in kg — the system applies gross, mid, or final tare based on where the truck is in the workflow.
        </p>
        <LiveWeighbridgeHUD className="mb-4" testId="weighbridge-hud-inward" />
        <WeightRecordingList items={weightRecordingQueue} onSaved={load} focusId={focusPurchaseId} />
      </SectionCard>

      {canRouteTrucks && weighGrossReady.length > 0 && (
        <SectionCard title={`Route after Gross Weigh (${weighGrossReady.length})`} testId="route-after-gross">
          <p className="text-sm text-muted-foreground mb-4">
            Purchases with gross weight recorded. Send to lab for incoming quality analysis, or skip lab and send directly for unloading approval.
          </p>
          <div className="space-y-3">
            {weighGrossReady.map((p) => (
              <RouteRow key={p.id} purchase={p} supervisors={supervisors} onSaved={load} />
            ))}
          </div>
        </SectionCard>
      )}
      </div>
        </TabsContent>

        <TabsContent value="outward" className="space-y-6 mt-0">
          <LiveWeighbridgeHUD className="mb-4" testId="weighbridge-hud-outward" />
          <MobilePillTabBar
            tabs={[
              { id: "tare", label: "Tare (empty)", badge: outwardQueue.tare?.length || 0 },
              { id: "gross", label: "Gross (loaded)", badge: outwardQueue.gross?.length || 0 },
            ]}
            value={outwardSubTab}
            onChange={setOutwardSubTab}
            className="mb-2"
            testId="weighbridge-outward-subtabs"
          />
          <div className="hidden lg:block space-y-6">
            <SectionCard title="Outward — record tare (empty truck)" testId="outward-tare-queue">
              <p className="text-xs text-muted-foreground mb-3">
                After sales supervisor links party & contract, record empty-truck tare weight before loading.
              </p>
              <OutwardWeightList items={outwardQueue.tare || []} kind="tare" onSaved={load} focusId={focusDispatchId} />
            </SectionCard>
            <SectionCard title="Outward — record gross (loaded truck)" testId="outward-gross-queue">
              <p className="text-xs text-muted-foreground mb-3">
                After loading supervisor confirms loading, record loaded gross weight. Net is calculated automatically.
              </p>
              <OutwardWeightList items={outwardQueue.gross || []} kind="gross" onSaved={load} focusId={focusDispatchId} />
            </SectionCard>
          </div>
          {outwardSubTab === "tare" && (
            <SectionCard title="Outward — record tare (empty truck)" testId="outward-tare-queue-mobile" className="lg:hidden">
              <p className="text-xs text-muted-foreground mb-3">
                After sales supervisor links party & contract, record empty-truck tare weight before loading.
              </p>
              <OutwardWeightList items={outwardQueue.tare || []} kind="tare" onSaved={load} focusId={focusDispatchId} />
            </SectionCard>
          )}
          {outwardSubTab === "gross" && (
            <SectionCard title="Outward — record gross (loaded truck)" testId="outward-gross-queue-mobile" className="lg:hidden">
              <p className="text-xs text-muted-foreground mb-3">
                After loading supervisor confirms loading, record loaded gross weight. Net is calculated automatically.
              </p>
              <OutwardWeightList items={outwardQueue.gross || []} kind="gross" onSaved={load} focusId={focusDispatchId} />
            </SectionCard>
          )}
        </TabsContent>

        <TabsContent value="register" className="mt-0 space-y-4">
          <MobilePillTabBar
            tabs={[
              { id: "inward", label: "Inward register", badge: weighmentRegisterTabCount },
              { id: "outward", label: "Outward register", badge: outwardRegister.length || 0 },
            ]}
            value={registerSubTab}
            onChange={setRegisterSubTab}
            testId="weighbridge-register-subtabs"
          />
          <div className={registerSubTab === "outward" ? "hidden lg:block" : undefined}>
          <SectionCard title="Inward weighment register" testId="weighment-register">
            <p className="text-xs text-muted-foreground mb-3">
              All trucks with gross, tare, net, or mid-segment weights recorded at the bridge. Click a row for photos and weigh history.
              {isAdmin ? " Admin can edit purchase contract links here." : ""}
            </p>
            <DataTableBlock
              title=""
              wrapInSectionCard={false}
              columns={WEIGHMENT_REGISTER_COLUMNS}
              table={weighmentList.table}
              rows={weighmentRows}
              loading={weighmentList.loading}
              totalCount={weighmentList.meta.count}
              testId="weighment-register-filters"
              pagination={{ ...weighmentList.pagination, pageSizeOptions: LIST_PAGE_SIZE_OPTIONS }}
              minWidth="0"
              renderTrailingHeader={() => (
                <span className="text-[10px] uppercase tracking-wide text-muted-foreground">Actions</span>
              )}
              renderRow={(p) => (
                <tr
                  key={p.id}
                  className={`border-b border-border/60 hover:bg-muted/20 cursor-pointer ${
                    String(registerSelectedId) === String(p.id) ? "bg-sky-50/60" : ""
                  }`}
                  onClick={() => {
                    setRegisterSelectedId(p.id);
                    setRegisterEditOpen(false);
                  }}
                >
                  <td className="py-2 px-2 text-xs whitespace-nowrap">{formatListDate(p.created_at) || "—"}</td>
                  <td className="py-2 px-2 text-xs font-medium uppercase">{p.vehicle_number || "—"}</td>
                  <td className="py-2 px-2 text-xs font-mono">{p.gate_pass_code || "—"}</td>
                  <td className="py-2 px-2 text-xs">{p.party_name}</td>
                  <td className="py-2 px-2 text-xs">{p.variety}</td>
                  <td className="py-2 px-2 text-xs tabular">{p.gross_weight_qtl ?? "—"}</td>
                  <td className="py-2 px-2 text-xs tabular">{p.tare_weight_qtl ?? "—"}</td>
                  <td className="py-2 px-2 text-xs tabular">{p.net_weight_qtl ?? "—"}</td>
                  <td className="py-2 px-2 text-xs tabular">{p.party_net_weight_qtl ?? "—"}</td>
                  <td className="py-2 px-2 text-xs whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                    <div className="flex items-center justify-end gap-1">
                      {canAdminEditPurchaseLink(p) ? (
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          className="h-8 text-xs"
                          data-testid={`purchase-link-edit-${p.id}`}
                          onClick={() => openPurchaseLinkEdit(p)}
                        >
                          {String(registerLinkEditId) === String(p.id) ? "Close" : "Edit link"}
                        </Button>
                      ) : null}
                      <WeighmentReceiptActions
                        compact
                        purchaseId={p.id}
                        label={p.gate_pass_code || p.vehicle_number}
                        canEdit={canEditWeights}
                        onEdit={() => {
                          setRegisterSelectedId(p.id);
                          closePurchaseLinkEdit();
                          setRegisterEditOpen(true);
                        }}
                      />
                    </div>
                  </td>
                </tr>
              )}
            />

            {registerSelected && (
              <div className="mt-4 rounded-md border border-border bg-muted/10 p-4 space-y-4 min-w-0">
                <div className="min-w-0">
                  <p className="text-sm font-medium">{registerSelected.party_name}</p>
                  <PurchaseTraceLine purchase={registerSelected} className="mt-0.5" />
                  <p className="text-[10px] text-muted-foreground mt-1 font-mono">{registerSelected.code}</p>
                </div>

                <WeighEvidencePanel purchaseId={registerSelected.id} testId="inward-weigh-evidence" />

                {isAdmin && String(registerLinkEditId) === String(registerSelected.id) && (
                  <div className="space-y-3" data-testid="inward-purchase-link-editor">
                    {registerLinkLoading ? (
                      <p className="text-sm text-muted-foreground py-4 text-center">Loading contracts…</p>
                    ) : registerLinkPurchase ? (
                      <>
                        <AccountsLinkedContractsEditor
                          ref={registerLinkEditorRef}
                          purchase={registerLinkPurchase}
                          canEdit
                          onSaved={async () => {
                            const { data } = await api.get(`/purchases/${registerLinkPurchase.id}`);
                            setRegisterLinkPurchase(data);
                            weighmentList.reload();
                            load();
                          }}
                        />
                        <div className="flex flex-wrap items-center gap-2">
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            className="text-xs"
                            disabled={registerLinkSaving}
                            onClick={closePurchaseLinkEdit}
                          >
                            Cancel
                          </Button>
                          <Button
                            type="button"
                            size="sm"
                            className="text-xs bg-bansal-sage hover:bg-bansal-sageDark text-white"
                            disabled={registerLinkSaving}
                            data-testid="inward-save-purchase-links"
                            onClick={async () => {
                              try {
                                setRegisterLinkSaving(true);
                                const ok = await registerLinkEditorRef.current?.save?.();
                                if (ok) closePurchaseLinkEdit();
                              } finally {
                                setRegisterLinkSaving(false);
                              }
                            }}
                          >
                            {registerLinkSaving ? "Saving…" : "Save linked contracts"}
                          </Button>
                        </div>
                      </>
                    ) : (
                      <p className="text-sm text-muted-foreground py-4 text-center">Could not load purchase.</p>
                    )}
                  </div>
                )}

                {canEditWeights && registerEditOpen && (
                  <AdminWeighCorrection
                    purchase={registerSelected}
                    onSaved={() => {
                      load();
                      weighmentList.reload();
                      setRegisterEditOpen(false);
                    }}
                  />
                )}
              </div>
            )}
          </SectionCard>
          </div>

          <div className={registerSubTab === "inward" ? "hidden lg:block" : undefined}>
          <SectionCard title="Outward weighment register" testId="outward-weighment-register" className="mt-0 lg:mt-6">
            <p className="text-xs text-muted-foreground mb-3">
              Outward rice dispatches with tare or gross recorded. Click a row for photos.
              {canEditWeights ? " Use the pencil to correct a recorded tare or gross." : ""}
            </p>
            <DataTableBlock
              title=""
              wrapInSectionCard={false}
              columns={OUTWARD_WEIGHMENT_REGISTER_COLUMNS}
              table={outwardRegisterTable}
              testId="outward-weighment-register-filters"
              minWidth="720px"
              emptyMessage="No outward weighment records."
              renderTrailingHeader={() => (
                <span className="text-[10px] uppercase tracking-wide text-muted-foreground">Actions</span>
              )}
              renderRow={(d) => (
                <tr
                  key={d.id}
                  className={`border-b border-border/60 hover:bg-muted/20 cursor-pointer ${
                    String(outwardRegisterSelectedId) === String(d.id) ? "bg-sky-50/60" : ""
                  }`}
                  onClick={() => {
                    setOutwardRegisterSelectedId(d.id);
                    setOutwardRegisterEditOpen(false);
                  }}
                >
                  <td className="py-2 px-2 whitespace-nowrap">
                    {formatListDate(d.gross_weighed_at || d.tare_weighed_at || d.created_at) || "—"}
                  </td>
                  <td className="py-2 px-2 font-medium uppercase">{d.vehicle_number || "—"}</td>
                  <td className="py-2 px-2 font-mono">{d.gate_pass_code || "—"}</td>
                  <td className="py-2 px-2">{d.party_name || "—"}</td>
                  <td className="py-2 px-2 tabular">
                    <div>{d.tare_weight_qtl ?? "—"}</div>
                    {d.tare_weighed_at && (
                      <div className="text-[10px] text-muted-foreground">{formatIndiaDateTime(d.tare_weighed_at)}</div>
                    )}
                  </td>
                  <td className="py-2 px-2 tabular">
                    <div>{d.gross_weight_qtl ?? "—"}</div>
                    {d.gross_weighed_at && (
                      <div className="text-[10px] text-muted-foreground">{formatIndiaDateTime(d.gross_weighed_at)}</div>
                    )}
                  </td>
                  <td className="py-2 px-2 tabular">{d.net_weight_qtl ?? "—"}</td>
                  <td className="py-2 px-2 font-mono">{d.sales_voucher_code || "—"}</td>
                  <td className="py-2 px-2 text-right" onClick={(e) => e.stopPropagation()}>
                    <OutwardWeighmentReceiptActions
                      dispatchId={d.id}
                      label={d.code}
                      compact
                      canEdit={canEditWeights && (d.tare_weight_qtl != null || d.gross_weight_qtl != null)}
                      onEdit={() => {
                        setOutwardRegisterSelectedId(d.id);
                        setOutwardRegisterEditOpen(true);
                      }}
                    />
                  </td>
                </tr>
              )}
            />
            {outwardRegisterSelectedId ? (
              <div className="mt-4 space-y-4 rounded-md border border-border bg-muted/10 p-4">
                <WeighEvidencePanel dispatchId={outwardRegisterSelectedId} testId="outward-weigh-evidence" />
                {canEditWeights && outwardRegisterEditOpen ? (
                  <OutwardWeighCorrection
                    dispatch={outwardRegister.find((row) => String(row.id) === String(outwardRegisterSelectedId))}
                    onSaved={() => {
                      load();
                      setOutwardRegisterEditOpen(false);
                    }}
                  />
                ) : null}
              </div>
            ) : null}
          </SectionCard>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}

function RouteRow({ purchase: p, supervisors, onSaved }) {
  const [routing, setRouting] = useState(false);
  const [supervisorId, setSupervisorId] = useState("");

  const assign = async (assignmentType) => {
    if (!supervisorId) {
      toast.error("Select unloading supervisor");
      return;
    }
    const selectedSupervisor = supervisors.find((u) => String(u.id) === String(supervisorId));
    const selectedLabel = selectedSupervisor?.name || selectedSupervisor?.email || `ID ${supervisorId}`;
    try {
      setRouting(true);
      await api.post(`/purchases/${p.id}/assign`, {
        purchase_id: Number(p.id),
        assigned_to: String(supervisorId),
        assignment_type: assignmentType,
      });
      toast.success(
        assignmentType === "PRE_ARRIVAL_LAB"
          ? `Assigned to ${selectedLabel} for pre-arrival lab`
          : `Assigned to ${selectedLabel} for direct unloading`,
      );
      setSupervisorId("");
      onSaved?.();
    } catch (err) {
      toast.error(extractError(err));
    } finally {
      setRouting(false);
    }
  };

  return (
    <div className="border border-border rounded-md p-4 flex flex-col gap-3 bg-muted/50">
      <div className="flex flex-col sm:flex-row sm:items-center gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-mono text-xs text-muted-foreground">{p.code}</span>
            <StatePill state={p.state} />
          </div>
          <PurchaseTraceLine purchase={p} className="mt-0.5" />
          <div className="text-sm font-medium mt-1">{p.party_name} · {p.variety}</div>
          <div className="text-xs text-muted-foreground mt-0.5">
            Gross: {formatWeightKg(p.gross_weight_qtl) ?? "—"}
          </div>
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto_auto] gap-2 items-end">
        <div>
          <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Assign to unloading supervisor</Label>
          <Select value={supervisorId} onValueChange={setSupervisorId}>
            <SelectTrigger className="mt-1.5">
              <SelectValue placeholder="Select supervisor…" />
            </SelectTrigger>
            <SelectContent>
              {supervisors.map((s) => (
                <SelectItem key={s.id} value={String(s.id)}>
                  {s.name || s.email} ({s.email})
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        {p.lab_required !== false && (
          <Button
            type="button"
            onClick={() => assign("PRE_ARRIVAL_LAB")}
            disabled={routing || !supervisorId}
            className="bg-bansal-sage hover:bg-bansal-sageDark text-white min-h-[44px]"
          >
            {routing ? "Assigning…" : "For Pre-arrival Lab"}
          </Button>
        )}
        <Button
          type="button"
          variant="outline"
          onClick={() => assign("DIRECT_UNLOADING")}
          disabled={routing || !supervisorId}
          className="min-h-[44px]"
        >
          {p.lab_required === false ? "For Direct Unloading" : "Skip Lab → Unloading"}
        </Button>
      </div>
    </div>
  );
}

function OutwardWeightList({ items, kind, onSaved, focusId }) {
  if (!items?.length) {
    return (
      <div className="text-sm text-muted-foreground py-8 text-center">
        No outward trucks awaiting {kind} weigh.
      </div>
    );
  }
  return (
    <div className="space-y-4">
      {items.map((d) => (
        <OutwardWeighRow
          key={d.id}
          dispatch={d}
          kind={kind}
          onSaved={onSaved}
          focused={String(focusId) === String(d.id)}
        />
      ))}
    </div>
  );
}

function OutwardWeighRow({ dispatch: d, kind, onSaved, focused }) {
  const { user } = useAuth();
  const canOverride = hasRole(user, ["admin", "manager"]);
  const live = useLiveScale();
  const captureBlocked = isCloudScaleCaptureBlocked(live, { allowUnstable: canOverride });
  const [weight, setWeight] = useState("");
  const [capturing, setCapturing] = useState(false);
  const [scaleCapture, setScaleCapture] = useState(null);
  const weightInputRef = useRef(null);
  const rowRef = useRef(null);

  useEffect(() => {
    if (!focused || !rowRef.current) return;
    rowRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
  }, [focused]);

  const fetchFromScale = async () => {
    try {
      setCapturing(true);
      const capture = await fetchScaleWeightAndCameraSnapshots({
        entityType: "outward",
        entityCode: d.code,
        gatePassCode: d.gate_pass_code || d.code,
        kind,
        allowUnstable: canOverride && live.agent_status === "ONLINE" && !live.is_stable,
      });
      setWeight(String(capture.weightKg));
      applyWeightToInputElement(weightInputRef.current, capture.weightKg);
      setScaleCapture(scaleCaptureState(capture));
      toast.success(`Scale read: ${formatKg(capture.weightKg)} kg`);
      if (capture.photoWarning) {
        toast.warning(capture.photoWarning);
      } else {
        toast.message(`Cameras saved · ${formatIndiaDateTime(capture.weighedAt) || "now"}`);
      }
    } catch (err) {
      toast.error(err?.message || extractError(err) || "Scale read failed");
    } finally {
      setCapturing(false);
    }
  };

  const recordWeight = async (source) => {
    let capture = scaleCapture;
    if (!capture) {
      try {
        setCapturing(true);
        capture = await fetchScaleWeightAndCameraSnapshots({
          entityType: "outward",
          entityCode: d.code,
          gatePassCode: d.gate_pass_code || d.code,
          kind,
          allowUnstable: canOverride,
        });
        setWeight(String(capture.weightKg));
        applyWeightToInputElement(weightInputRef.current, capture.weightKg);
        setScaleCapture(scaleCaptureState(capture));
      } catch (err) {
        if (!parseFloat(weight)) {
          toast.error(err?.message || extractError(err) || "Scale capture failed");
          setCapturing(false);
          return;
        }
        toast.warning(err?.message || "Recording typed weight without camera archive");
      }
    }
    const wKg = weightKgToRecord(weight, capture);
    if (!Number.isFinite(wKg) || wKg <= 0) {
      setCapturing(false);
      toast.error("Enter a valid weight in kg");
      return;
    }
    const wQtl = kgToQtl(wKg);
    if (wQtl == null) {
      setCapturing(false);
      toast.error("Enter a valid weight in kg");
      return;
    }
    try {
      setCapturing(true);
      const { data } = await api.post(`/outward-dispatches/${d.id}/weigh`, {
        kind,
        weight_qtl: wQtl,
        source: capture ? "device" : source,
        weighed_at: capture?.weighedAt || undefined,
        camera1_snapshot_url: capture?.camera1SnapshotUrl || undefined,
        camera2_snapshot_url: capture?.camera2SnapshotUrl || undefined,
      });
      if (kind === "gross") {
        toast.success(`Gross ${formatWeightKg(data.gross_weight_qtl)} — net ${formatWeightKg(data.net_weight_qtl)} → accounts`);
      } else {
        toast.success(`Tare ${formatWeightKg(data.tare_weight_qtl)} recorded — send to loading`);
      }
      setWeight("");
      setScaleCapture(null);
      onSaved?.();
    } catch (err) {
      toast.error(extractError(err));
    } finally {
      setCapturing(false);
    }
  };

  return (
    <div
      ref={rowRef}
      className={`rounded-lg border p-4 space-y-3 ${
        focused ? "border-bansal-sage ring-2 ring-bansal-sage/30" : "border-border"
      }`}
      data-testid={`outward-wb-row-${d.code}`}
    >
      <div className="flex items-start justify-between gap-3 min-w-0">
        <div className="min-w-0 space-y-1">
          <p className="text-sm font-medium truncate">{d.party_name || "—"}</p>
          <p className="text-base sm:text-lg font-semibold uppercase tracking-wide tabular">
            {d.vehicle_number ? String(d.vehicle_number).toUpperCase() : "—"}
          </p>
        </div>
        <div className="shrink-0 text-right space-y-1">
          <Pill tone="gold">{kind === "tare" ? "Tare weigh-in" : "Gross weigh-in"}</Pill>
          <p className="text-xs sm:text-sm text-muted-foreground tabular">
            {formatListDate(d.gate_pass_created_at || d.created_at) || "—"}
          </p>
          <p className="text-xs sm:text-sm text-muted-foreground font-mono">
            {d.gate_pass_code || "—"}
          </p>
        </div>
      </div>
      {scaleCapture && (
        <ScaleCaptureEvidence capture={scaleCapture} testId={`outward-capture-${kind}-${d.code}`} />
      )}
      <div className="flex flex-col sm:flex-row gap-2 sm:items-end">
        <div className="flex-1 min-w-0">
          <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">
            {kind === "tare" ? "Empty truck weight (kg)" : "Loaded truck weight (kg)"}
          </Label>
          <Input
            ref={weightInputRef}
            type="number"
            step="1"
            min="0"
            value={weight}
            onChange={(e) => {
              setWeight(e.target.value);
              setScaleCapture(null);
            }}
            className="mt-1 h-10"
            placeholder="Fetch scale (captures time + cameras)"
          />
        </div>
        <Button type="button" variant="outline" onClick={fetchFromScale} disabled={capturing || captureBlocked} className="min-h-[44px]">
          <Radio className="h-4 w-4 mr-1" /> {capturing ? "Capturing…" : captureBlocked ? "Scale moving" : "Capture Weight"}
        </Button>
        <Button
          type="button"
          onClick={() => recordWeight(scaleCapture ? "device" : "manual")}
          disabled={capturing}
          className="bg-bansal-sage hover:bg-bansal-sageDark text-white min-h-[44px]"
        >
          Record {kind}
        </Button>
      </div>
    </div>
  );
}

function WeightRecordingList({ items, onSaved, focusId }) {
  if (items.length === 0) {
    return (
      <div className="text-sm text-muted-foreground py-8 text-center">
        No trucks awaiting weight recording.
      </div>
    );
  }
  return (
    <div className="space-y-4">
      {items.map((p) => (
        <WeighRow key={p.id} purchase={p} onSaved={onSaved} focused={String(focusId) === String(p.id)} />
      ))}
    </div>
  );
}

function WeighRow({ purchase: p, kind: kindProp, onSaved, focused }) {
  const kind = kindProp ?? purchaseWeighKind(p) ?? "gross";
  const stepLabel = purchaseWeighStepLabel(p);
  const { user } = useAuth();
  const canOverride = hasRole(user, ["admin", "manager"]);
  const live = useLiveScale();
  const captureBlocked = isCloudScaleCaptureBlocked(live, { allowUnstable: canOverride });
  const [weight, setWeight] = useState("");
  const [capturing, setCapturing] = useState(false);
  const [scaleCapture, setScaleCapture] = useState(null);
  const weightInputRef = useRef(null);
  const rowRef = useRef(null);

  useEffect(() => {
    if (!focused || !rowRef.current) return;
    rowRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
  }, [focused]);

  const fetchFromScale = async () => {
    try {
      setCapturing(true);
      const capture = await fetchScaleWeightAndCameraSnapshots({
        entityType: "purchase",
        entityCode: p.code,
        gatePassCode: p.gate_pass_code || p.code,
        kind,
        allowUnstable: canOverride && live.agent_status === "ONLINE" && !live.is_stable,
      });
      setWeight(String(capture.weightKg));
      applyWeightToInputElement(weightInputRef.current, capture.weightKg);
      setScaleCapture(scaleCaptureState(capture));
      toast.success(`Scale read: ${formatKg(capture.weightKg)} kg`);
      if (capture.photoWarning) {
        toast.warning(capture.photoWarning);
      } else {
        toast.message(`Cameras saved · ${formatIndiaDateTime(capture.weighedAt) || "now"}`);
      }
    } catch (err) {
      toast.error(err?.message || extractError(err) || "Scale read failed");
    } finally {
      setCapturing(false);
    }
  };

  const recordWeight = async (source) => {
    let capture = scaleCapture;
    if (!capture) {
      try {
        setCapturing(true);
        capture = await fetchScaleWeightAndCameraSnapshots({
          entityType: "purchase",
          entityCode: p.code,
          gatePassCode: p.gate_pass_code || p.code,
          kind,
          allowUnstable: canOverride,
        });
        setWeight(String(capture.weightKg));
        applyWeightToInputElement(weightInputRef.current, capture.weightKg);
        setScaleCapture(scaleCaptureState(capture));
      } catch (err) {
        if (!parseFloat(weight)) {
          toast.error(err?.message || extractError(err) || "Scale capture failed");
          setCapturing(false);
          return;
        }
        toast.warning(err?.message || "Recording typed weight without camera archive");
      }
    }
    const wKg = weightKgToRecord(weight, capture);
    if (!Number.isFinite(wKg) || wKg <= 0) {
      setCapturing(false);
      toast.error("Enter a valid weight in kg");
      return;
    }
    const wQtl = kgToQtl(wKg);
    if (wQtl == null) {
      setCapturing(false);
      toast.error("Enter a valid weight in kg");
      return;
    }
    try {
      setCapturing(true);
      const payload = {
        purchase_id: p.id,
        kind,
        weight_qtl: wQtl,
        source: capture ? "device" : source,
        weighed_at: capture?.weighedAt || undefined,
        camera1_snapshot_url: capture?.camera1SnapshotUrl || undefined,
        camera2_snapshot_url: capture?.camera2SnapshotUrl || undefined,
      };
      const { data } = await api.post("/purchases/weigh", payload);
      const recordedQtl = kind === "gross" ? data.gross_weight_qtl : data.tare_weight_qtl;
      const recordedKg = formatWeightKg(recordedQtl);
      if (kind === "mid") {
        const lastSeg = (data.loose_weigh_segments || []).slice(-1)[0];
        toast.success(
          `Mid ${recordedKg} — segment net ${formatWeightKg(lastSeg?.segment_net_qtl) ?? "?"}; truck back to unloading`,
        );
      } else if (kind === "tare" && p.unload_by_qtl) {
        toast.success(`Final tare ${recordedKg} — total net ${formatWeightKg(data.net_weight_qtl) ?? "?"} → accounts`);
      } else {
        toast.success(
          kind === "tare"
            ? `Tare ${recordedKg} — sent to accounts for final calculation`
            : `${kind.toUpperCase()} recorded: ${recordedKg}`,
        );
      }
      setWeight("");
      setScaleCapture(null);
      onSaved?.();
    } catch (err) {
      toast.error(extractError(err));
    } finally {
      setCapturing(false);
    }
  };

  return (
    <div
      ref={rowRef}
      className={`border rounded-md p-4 flex flex-col gap-4 bg-muted/50 ${
        focused ? "border-bansal-sage ring-2 ring-bansal-sage/30" : "border-border"
      }`}
      data-testid={`wb-row-${p.code}`}
    >
      <div className="flex items-start justify-between gap-3 min-w-0">
        <div className="min-w-0 space-y-1">
          <p className="text-sm font-medium truncate">{p.party_name || "—"}</p>
          <p className="text-base sm:text-lg font-semibold uppercase tracking-wide tabular">
            {p.vehicle_number ? String(p.vehicle_number).toUpperCase() : "—"}
          </p>
        </div>
        <div className="shrink-0 text-right space-y-1">
          <Pill tone="gold">{stepLabel}</Pill>
          <p className="text-xs sm:text-sm text-muted-foreground tabular">
            {formatListDate(p.created_at || p.gate_pass_created_at) || "—"}
          </p>
          <p className="text-xs sm:text-sm text-muted-foreground font-mono">
            {p.gate_pass_code || "—"}
          </p>
        </div>
      </div>

      {scaleCapture && (
        <ScaleCaptureEvidence capture={scaleCapture} testId={`capture-${kind}-${p.code}`} />
      )}

      <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto_auto] gap-2 items-end">
        <div>
          <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">
            Bridge weight (kg)
          </Label>
          <Input
            ref={weightInputRef}
            type="number"
            step="1"
            min="0"
            value={weight}
            onChange={(e) => {
              setWeight(e.target.value);
              setScaleCapture(null);
            }}
            placeholder="Fetch scale (captures time + cameras)"
            className="mt-1.5"
            data-testid={`weight-${kind}-${p.code}`}
          />
        </div>
        <Button
          type="button"
          variant="outline"
          onClick={fetchFromScale}
          disabled={capturing || captureBlocked}
          className="min-h-[44px] w-full sm:w-auto"
          data-testid={`fetch-scale-${kind}-${p.code}`}
        >
          <Radio className="h-4 w-4 mr-1.5 text-bansal-gold" />
          {capturing ? "Capturing…" : captureBlocked ? "Scale moving" : "Capture Weight"}
        </Button>
        <Button
          type="button"
          onClick={() => recordWeight(scaleCapture ? "device" : "manual")}
          disabled={capturing}
          className="bg-bansal-ink hover:bg-bansal-charcoal text-bansal-ivory min-h-[44px] w-full sm:w-auto"
          data-testid={`weigh-${kind}-${p.code}`}
        >
          <Scale className="h-4 w-4 mr-1.5 text-bansal-gold" />
          {capturing ? "Saving…" : "Record weight"}
        </Button>
      </div>
      <p className="text-[10px] text-muted-foreground">
        <strong>Capture Weight</strong> locks kg, time, and camera stills when the cloud scale is STABLE (managers can override). Then{" "}
        <strong>Record weight</strong>.
      </p>
    </div>
  );
}

function ScaleCaptureEvidence({ capture, testId }) {
  if (!capture) return null;
  const src1 = weighPhotoDisplaySrc(capture.camera1ViewUrl || capture.camera1SnapshotUrl);
  const src2 = weighPhotoDisplaySrc(capture.camera2ViewUrl || capture.camera2SnapshotUrl);
  const hasPhotos = Boolean(src1 && src2);
  return (
    <div className="rounded-md border border-border bg-background/60 p-2 space-y-1.5" data-testid={testId}>
      <p className="text-[10px] uppercase tracking-widest text-muted-foreground">
        Captured at fetch · {formatIndiaDateTime(capture.weighedAt) || capture.weighedAt}
      </p>
      {capture.photoWarning ? (
        <p className="text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded px-2 py-1">
          {capture.photoWarning}
        </p>
      ) : null}
      {hasPhotos ? (
        <div className="grid grid-cols-2 gap-2">
          <a href={src1} target="_blank" rel="noreferrer" className="block">
            <img
              src={src1}
              alt="Camera 1 at weigh"
              className="h-24 w-full object-cover rounded border border-border"
            />
            <span className="text-[10px] text-muted-foreground">Angle 1 · open original</span>
          </a>
          <a href={src2} target="_blank" rel="noreferrer" className="block">
            <img
              src={src2}
              alt="Camera 2 at weigh"
              className="h-24 w-full object-cover rounded border border-border"
            />
            <span className="text-[10px] text-muted-foreground">Angle 2 · open original</span>
          </a>
        </div>
      ) : (
        <p className="text-[11px] text-muted-foreground">No camera images attached to this fetch.</p>
      )}
    </div>
  );
}

function Field({ label, required, className, children }) {
  return (
    <div className={className}>
      <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">
        {label}
        {required && <span className="text-red-500 ml-0.5">*</span>}
      </Label>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}
